import { Trans, useLingui } from "@lingui/react/macro";
import {
  Avatar,
  Box,
  Flex,
  Grid,
  Group,
  LoadingOverlay,
  Paper,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { createFileRoute, redirect, useBlocker } from "@tanstack/react-router";

import useUpdateEmployeeForm from "@/features/employees/update-employee/use-update-employee-form";
import {
  getGetEmployeeQueryOptions,
  useGetEmployee,
} from "@/shared/api/generated/employee";
import { useGetLocations } from "@/shared/api/generated/locations";

export const Route = createFileRoute(
  "/_app/(single-location)/museum/$museumId/employees/$employeeId/",
)({
  beforeLoad: async ({ params, context }) => {
    const employee = await context.queryClient.ensureQueryData(
      getGetEmployeeQueryOptions(params.employeeId),
    );

    if (!employee.data.locations?.some((l) => l.id === params.museumId)) {
      redirect({
        to: "/unauthorized",
        throw: true,
      });
    }
  },
  component: EmployeeDetailsPage,
});

function EmployeeDetailsPage() {
  const { employeeId } = Route.useParams();

  const { i18n, t } = useLingui();

  const { data, isLoading } = useGetEmployee(employeeId);
  const { data: locations } = useGetLocations();
  const { form, handleSubmit } = useUpdateEmployeeForm(employeeId, data?.data);

  useBlocker({
    blockerFn: () =>
      window.confirm(
        t`Det er gjort endringer i skjemaet som ikke er lagret. Trykk "OK" for å forkaste endringene.`,
      ),
    condition: form.isDirty(),
  });

  return (
    <Box p="lg">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Group justify="space-between">
          <h1>
            {isLoading ? (
              <Skeleton h={40} w={200} />
            ) : (
              <>
                {data?.data.firstName} {data?.data.lastName ?? ""}
              </>
            )}
          </h1>

          <Stack gap={0}>
            <Text size="sm" c="dimmed" style={{ textAlign: "right" }}>
              {data?.data.createdAt ? (
                <>
                  <Trans>Opprettet:</Trans>{" "}
                  {i18n.date(data?.data.createdAt, {
                    dateStyle: "short",
                    timeStyle: "long",
                  })}
                </>
              ) : null}
            </Text>
            <Text size="sm" c="dimmed">
              {data?.data.updatedAt ? (
                <>
                  <Trans>Sist endret:</Trans>{" "}
                  {i18n.date(data?.data.updatedAt, {
                    dateStyle: "short",
                    timeStyle: "long",
                  })}
                </>
              ) : null}
            </Text>
          </Stack>
        </Group>

        <Grid align="stretch" pos="relative">
          <LoadingOverlay visible={isLoading} />

          <Grid.Col span={4}>
            <Paper p="xs" h="100%">
              <Flex justify="center">
                <Avatar color="primary" size={125}>
                  {data?.data.firstName?.[0]}
                  {data?.data.lastName?.[0] ?? ""}
                </Avatar>
              </Flex>

              <Stack>
                <TextInput
                  label={t`Fornavn`}
                  placeholder={t`Fornavn`}
                  key={form.key("firstName")}
                  {...form.getInputProps("firstName")}
                  withAsterisk
                />
                <TextInput
                  label={t`Etternavn`}
                  placeholder={t`Etternavn`}
                  key={form.key("lastName")}
                  {...form.getInputProps("lastName")}
                />
              </Stack>
            </Paper>
          </Grid.Col>

          <Grid.Col span={6}>
            <Stack h="100%">
              <Paper p="xs">
                <Text size="sm" mb="sm">
                  <Trans>Kontaktinformasjon</Trans>
                </Text>

                <Group grow align="start">
                  <TextInput
                    label={t`E-post`}
                    placeholder={t`Velg e-post`}
                    key={form.key("email")}
                    {...form.getInputProps("email")}
                    withAsterisk
                  />
                  <TextInput
                    label={t`Telefon`}
                    placeholder={t`Velg telefon`}
                    key={form.key("phone")}
                    {...form.getInputProps("phone")}
                  />
                </Group>
              </Paper>
              <Paper p="xs" h="100%">
                <Text size="sm" mb="sm">
                  <Trans>Arbeidsinformasjon</Trans>
                </Text>

                <Select
                  label={t`Lokasjon`}
                  placeholder={t`Velg lokasjon`}
                  data={locations?.data?.map((l) => ({
                    value: l.id,
                    label: l.name,
                  }))}
                  key={form.key("location")}
                  {...form.getInputProps("location")}
                  readOnly
                />
              </Paper>
            </Stack>
          </Grid.Col>
        </Grid>
      </form>
    </Box>
  );
}
